import React from 'react';
import PolicyLayout from './PolicyLayout';
import useDocumentMeta from '../hooks/useDocumentMeta';

const LAST_UPDATED = '2026-06-02';

const TermsOfUse = () => {
  useDocumentMeta({
    title: 'Terms of Use — Factory OS',
    description:
      'The terms that govern your use of the Factory OS website, platform, and manufacturing services — including quote estimates, submission responsibilities, manufacturing limitations, and liability.',
    path: '/terms',
  });

  return (
    <PolicyLayout
      title="Terms of Use"
      lastUpdated={LAST_UPDATED}
      lead="These terms govern your use of the Factory OS website, platform, and manufacturing services. They are written to be readable. The placeholders in brackets will be finalized before any commercial agreement."
    >
      <h2>1. Acceptance</h2>
      <p>
        By accessing or using <code>factory-os.com</code>, <code>app.factory-os.com</code>, <code>demo.factory-os.com</code>, or any other service operated by Factory OS (collectively, the &quot;Service&quot;), you agree to these Terms of Use (the &quot;Terms&quot;). If you do not agree, please do not use the Service.
      </p>

      <h2>2. What the Service is</h2>
      <p>
        Factory OS provides an engineering operating system for hardware companies. The Service includes a marketing website, a software platform for capturing requirements, designs, manufacturing data, and test results, and (subject to availability) in-house manufacturing of parts you specifically request.
      </p>
      <p>
        Some features are described as in development. They may change, become available, or be removed without notice while the product matures.
      </p>

      <h2>3. Account and acceptable use</h2>
      <p>You agree to:</p>
      <ul>
        <li>Provide accurate information when you create an account or contact us.</li>
        <li>Keep your account credentials confidential and be responsible for activity under your account.</li>
        <li>Use the Service only for lawful purposes and in accordance with these Terms.</li>
      </ul>
      <p>You agree not to:</p>
      <ul>
        <li>Attempt to access systems or data you are not authorized to access.</li>
        <li>Interfere with, disrupt, or overload the Service or its infrastructure.</li>
        <li>Probe, scan, or test the vulnerability of the Service except through a coordinated disclosure that we have agreed to in writing.</li>
        <li>Reverse engineer, decompile, or copy the Service except where applicable law allows.</li>
        <li>Use the Service to violate the rights of others or applicable law.</li>
      </ul>

      <h2>4. Quote estimates are non-binding</h2>
      <div className="callout">
        <p>
          <strong>Quote estimates generated by the platform are non-binding indications, not contractual offers.</strong> They are calculated from the geometry, material, process, and assumptions available at the time and are intended to give you a fast cost signal during design.
        </p>
        <p>
          A binding price for a manufacturing run is established only when we send you a written quote (e.g., by email or through the platform) that references the specific design files, revision, quantity, lead time, and acceptance terms, and you accept it.
        </p>
      </div>

      <h2>5. Your files and submissions</h2>
      <p>
        You retain all intellectual-property rights in the files you upload (CAD models, drawings, scans, photos, test data, etc.). You grant Factory OS a limited, non-exclusive license to host, process, transmit, and display your files solely to provide the Service to you and your authorized team — for example, to render a model, run a DFM check, generate a quote, produce a drawing package, or fabricate a part you specifically request.
      </p>
      <p>
        We do not use your files to train models, build a catalog of parts, or generate any internal asset for ourselves or any third party without your explicit, written permission.
      </p>

      <h3>You are responsible for what you upload</h3>
      <p>
        By uploading a file or submitting a part for manufacture, you represent that:
      </p>
      <ul>
        <li>You own the file or have all necessary rights, licenses, and permissions to upload it and to have a part made from it.</li>
        <li>Your submission does not infringe any third party&apos;s intellectual-property, trade-secret, contractual, or privacy rights.</li>
        <li>Your submission is not subject to export controls, sanctions, defense-trade restrictions (e.g., ITAR), or other regulatory regimes that would prohibit our handling it, unless we have agreed in writing to receive controlled material.</li>
      </ul>

      <h3>Prohibited submissions</h3>
      <p>You may not submit, upload, or request the manufacture of:</p>
      <ul>
        <li>Firearms, firearm components, suppressors, ammunition components, or related items.</li>
        <li>Counterfeit items intended to be passed off as a genuine third-party product.</li>
        <li>Items that would infringe a valid patent, trademark, copyright, or trade secret.</li>
        <li>Items subject to export controls or sanctions that we have not agreed in writing to handle.</li>
        <li>Items intended for use in violation of applicable law.</li>
        <li>Safety-critical or life-critical components (medical implants, aerospace flight-critical hardware, nuclear, etc.) unless we have agreed in writing in advance to the specific use and quality regime.</li>
      </ul>

      <h2>6. Manufacturing — tolerances and limitations</h2>
      <p>
        Parts produced by the Factory OS shop are made using the process you select (e.g., FDM 3D printing, CNC milling, and other processes as they come online). Each process has inherent tolerances, surface finishes, material variability, and limitations. Unless we agree on a specific tolerance, inspection, or certification regime in writing, parts are produced to the standard tolerances of the chosen process.
      </p>
      <p>
        Where you supply a drawing with GD&amp;T, we will produce to that drawing on a best-effort basis given the process limits. Where you supply only a model, we will produce the model with standard tolerances. You are responsible for confirming a part is fit for its intended purpose before putting it into service.
      </p>

      <h2>7. No OEM affiliation unless stated</h2>
      <p>
        Factory OS is an independent service. We are not affiliated with, endorsed by, or a representative of any original equipment manufacturer (OEM) unless explicitly stated in writing. Use of any third-party name, trademark, or model number in a project description, file name, or quote request is descriptive only and does not imply OEM affiliation, approval, or warranty.
      </p>

      <h2>8. Site content and trademarks</h2>
      <p>
        The Service, including all text, graphics, logos, UI, and software, is owned by Factory OS or its licensors and is protected by intellectual-property laws. You may view and use the Service for its intended purpose, but you may not copy, modify, or redistribute it except as expressly permitted.
      </p>

      <h2>9. Disclaimer of warranties</h2>
      <p>
        Except as expressly stated in a separate written agreement, the Service is provided <strong>&quot;as is&quot;</strong> and <strong>&quot;as available&quot;</strong> without warranties of any kind, whether express, implied, or statutory, including warranties of merchantability, fitness for a particular purpose, non-infringement, and any warranty arising out of course of dealing or usage of trade.
      </p>

      <h2>10. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, in no event will Factory OS, its affiliates, or its service providers be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, revenue, data, or goodwill, arising out of or in connection with your use of the Service — even if advised of the possibility of such damages.
      </p>
      <p>
        Our aggregate liability for any claim arising out of or relating to the Service will not exceed the greater of (a) the amount you paid us for the Service in the [twelve (12)] months preceding the event giving rise to the claim, or (b) [one hundred U.S. dollars ($100)].
      </p>

      <h2>11. Indemnification</h2>
      <p>
        You agree to defend, indemnify, and hold harmless Factory OS and its personnel from and against any third-party claim, demand, loss, or expense (including reasonable legal fees) arising out of your breach of these Terms, your submissions, your violation of law, or your infringement of any third party&apos;s rights.
      </p>

      <h2>12. Termination</h2>
      <p>
        You may stop using the Service at any time. We may suspend or terminate access to the Service if you breach these Terms, if continued provision creates a legal or security risk, or if we cease offering the Service. Sections that by their nature should survive termination (e.g., IP, disclaimer, limitation of liability, indemnification, governing law) will survive.
      </p>

      <h2>13. Governing law and disputes</h2>
      <p>
        These Terms are governed by the laws of [State / Country placeholder], without regard to its conflict-of-laws principles. The parties consent to the exclusive jurisdiction of the state and federal courts located in [Venue placeholder] for any dispute that is not otherwise required to be resolved by another method (e.g., mandatory arbitration in your jurisdiction).
      </p>

      <h2>14. Changes</h2>
      <p>
        We may update these Terms from time to time. When we make a material change, we will update the &quot;Last updated&quot; date at the top and, where appropriate, notify you through the platform or by email. Continued use of the Service after a change means you accept the updated Terms.
      </p>

      <h2>15. Contact</h2>
      <p>
        Questions about these Terms? <a href="/contact">Contact us</a> or email hello@factory-os.com with "Legal" in the subject.
      </p>
    </PolicyLayout>
  );
};

export default TermsOfUse;
